class ProductVariantDefaultDropdownSelector extends HTMLElement {
  constructor() {
    super();
  }
  /**
   * Lifecycle method called when the element is inserted into the DOM.
   */
  connectedCallback() {
    this.section = this;
    this.selectedVariantTitle = this.section.querySelector(
      "#selected-variant-title"
    );

    this.dropdown = this.section.querySelector(
      "#product-variant-default-dropdown-selector"
    );

    this.dropdownChangeHandler = this.dropdownEventHandler.bind(this);

    this.loadSection();

    this.dropdown.addEventListener("change", this.dropdownChangeHandler);
  }

  /**
   * Lifecycle method called when the element is removed from the DOM.
   */
  disconnectedCallback() {
    this.dropdown.removeEventListener("change", this.dropdownChangeHandler);
  }

  /**
   * Loads the dropdown section, retrieves the selected value,
   * and updates the selected variant title
   */
  loadSection() {
    const value = this.dropdown.value;
    const option = this.getOptionByValue(value);
    if (!option) return;

    this.updateSelectedVariantTitle(value);
    const { variantHandle, variantIsSoldOut } = option.dataset;

    Shopify?.ProductGalleryFilterByVariantHandle(variantHandle);
    Shopify?.ProductOverviewSetAddToCartDisabledAttribute(variantIsSoldOut);
    Shopify?.ProductOverviewShowOutStockKlavyoButton(variantIsSoldOut);
    this.notifyVariantChange(option);
  }

  /**
   * Event handler for the dropdown change event.
   * Updates the selected variant title and filters the product gallery.
   * @param {Event} event - The change event object.
   */
  dropdownEventHandler(event) {
    const value = event.target.value;
    const option = this.getOptionByValue(value);
    if (!option) return;

    const { variantHandle, variantIsSoldOut } = option.dataset;
    this.updateSelectedVariantTitle(value);
    Shopify?.ProductGalleryFilterByVariantHandle(variantHandle);
    Shopify?.ProductOverviewSetAddToCartDisabledAttribute(variantIsSoldOut);
    Shopify?.ProductOverviewShowOutStockKlavyoButton(variantIsSoldOut);
    this.notifyVariantChange(option);
  }

  /**
   * Updates the inner HTML of the selected variant title element.
   * @param {string} title - The title to set.
   */
  updateSelectedVariantTitle(title) {
    this.selectedVariantTitle.innerHTML = title;
  }

  /**
   * Dispatches a custom event with the latest variant price so other components can react.
   * @param {HTMLOptionElement} option
   */
  notifyVariantChange(option) {
    const priceInCents = this.getNumericDatasetValue(
      option.dataset.variantPrice
    );
    if (priceInCents === null) return;

    const compareAtPriceInCents = this.getNumericDatasetValue(
      option.dataset.variantCompareAtPrice
    );

    const detail = {
      id: option.dataset.variantId,
      handle: option.dataset.variantHandle,
      title: option.value,
      price: priceInCents,
      isSoldOut: option.dataset.variantIsSoldOut === "true",
      compareAtPrice: compareAtPriceInCents,
    };

    Shopify?.ProductOverviewUpdateVariantPrice({
      price: priceInCents / 100,
      compareAtPrice: this.convertCentsToDollars(compareAtPriceInCents),
    });
    document.dispatchEvent(
      new CustomEvent("pdp:variant-price-update", { detail })
    );
  }

  /**
   * Converts cents into dollars.
   * @param {number|null} cents
   * @returns {number|null}
   */
  convertCentsToDollars(cents) {
    if (typeof cents !== "number" || Number.isNaN(cents)) return null;
    return cents / 100;
  }

  /**
   * Parses a numeric dataset value, returning null for empty or invalid values.
   * @param {string|undefined} value
   * @returns {number|null}
   */
  getNumericDatasetValue(value) {
    if (value === undefined || value === null || value === "") return null;
    const parsedValue = Number(value);
    return Number.isNaN(parsedValue) ? null : parsedValue;
  }

  /**
   * Returns the option that matches the provided value.
   * @param {string} value
   * @returns {HTMLOptionElement | undefined}
   */
  getOptionByValue(value) {
    return Array.from(this.dropdown.options).find(
      (option) => option.value === value
    );
  }
}

customElements.define(
  "product-variant-default-dropdown-selector",
  ProductVariantDefaultDropdownSelector
);
